/* eslint-disable @calm/react-intl/missing-formatted-message -- Preview-only copy is isolated from the connected Hubs locale catalogue. */
import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft } from "@fortawesome/free-solid-svg-icons/faArrowLeft";
import { faCheck } from "@fortawesome/free-solid-svg-icons/faCheck";
import { faChevronDown } from "@fortawesome/free-solid-svg-icons/faChevronDown";
import { faComment } from "@fortawesome/free-solid-svg-icons/faComment";
import { faCog } from "@fortawesome/free-solid-svg-icons/faCog";
import { faDesktop } from "@fortawesome/free-solid-svg-icons/faDesktop";
import { faMicrophone } from "@fortawesome/free-solid-svg-icons/faMicrophone";
import { faQuestionCircle } from "@fortawesome/free-solid-svg-icons/faQuestionCircle";
import { faShareSquare } from "@fortawesome/free-solid-svg-icons/faShareSquare";
import { faSignOutAlt } from "@fortawesome/free-solid-svg-icons/faSignOutAlt";
import { faSmile } from "@fortawesome/free-solid-svg-icons/faSmile";
import { faUsers } from "@fortawesome/free-solid-svg-icons/faUsers";
import { faVolumeUp } from "@fortawesome/free-solid-svg-icons/faVolumeUp";
import { faVrCardboard } from "@fortawesome/free-solid-svg-icons/faVrCardboard";
import { AppLogo } from "../misc/AppLogo";
import { ThemeToggle } from "../layout/ThemeToggle";
import immersiveMeetupPoster from "../../assets/images/i3/i3-immersive-meetup-poster.png";
import styles from "./PreviewExperience.scss";

const ATTENDEE_STEPS = ["device", "audio", "avatar"];
const ORGANIZER_STEPS = ["event-details", "event-space", "event-access", "event-review"];
const DEMO_STEPS = [...ATTENDEE_STEPS, ...ORGANIZER_STEPS, "event-dashboard", "space-editor", "room"];
const ATTENDEE_LABELS = { device: "Device", audio: "Audio", avatar: "Avatar" };
const ORGANIZER_LABELS = {
  "event-details": "Details",
  "event-space": "Space",
  "event-access": "Access",
  "event-review": "Review"
};
const MICROPHONE_OPTIONS = [
  { value: "default-microphone", label: "Default microphone" },
  { value: "headset-microphone", label: "Headset microphone" }
];
const SPEAKER_OPTIONS = [
  { value: "default-speakers", label: "Default speakers" },
  { value: "headset-speakers", label: "Headset speakers" }
];
const CAPACITY_OPTIONS = [
  { value: "24", label: "24 people" },
  { value: "50", label: "50 people" }
];
const SENSITIVITY_OPTIONS = [
  { value: "internal", label: "Internal" },
  { value: "confidential", label: "Confidential" },
  { value: "public", label: "Public" }
];
const DEFAULT_EVENT_DRAFT = {
  title: "i3 Immersive Meetup",
  date: "2026-09-10",
  time: "14:00",
  duration: "60",
  description: "A shared 3D event for presentations, conversation and product demonstrations.",
  coOrganizers: "alex@i3simulations.com",
  attendees: "design-team@i3simulations.com",
  sensitivity: "internal",
  space: "i3-atrium",
  access: "invite"
};

function getInitialEventDraft() {
  try {
    const savedDraft = window.sessionStorage.getItem("i3-meet-event-draft");
    return savedDraft ? { ...DEFAULT_EVENT_DRAFT, ...JSON.parse(savedDraft) } : DEFAULT_EVENT_DRAFT;
  } catch {
    return DEFAULT_EVENT_DRAFT;
  }
}

function getStepFromUrl() {
  const step = new URLSearchParams(window.location.search).get("demo");
  return DEMO_STEPS.includes(step) ? step : "device";
}

function formatEventDate(value) {
  return new Date(`${value}T12:00:00`).toLocaleDateString(undefined, {
    day: "numeric",
    month: "long",
    year: "numeric"
  });
}

function DemoHeader({ onExit, compact = false }) {
  return (
    <header className={styles.header}>
      <a className={styles.logo} href="/" aria-label="Return to i3 Meet home">
        <AppLogo />
      </a>
      <div className={styles.headerMeta}>
        {!compact && <span className={styles.previewBadge}>Interactive demo</span>}
        <ThemeToggle compact={compact} />
        <button className={styles.headerButton} type="button" onClick={onExit}>
          <FontAwesomeIcon icon={faSignOutAlt} />
          <span>Exit demo</span>
        </button>
      </div>
    </header>
  );
}

DemoHeader.propTypes = {
  onExit: PropTypes.func.isRequired,
  compact: PropTypes.bool
};

function Progress({ currentStep, steps, labels }) {
  const currentIndex = steps.indexOf(currentStep);
  return (
    <ol className={styles.progress} aria-label={`Setup progress, step ${currentIndex + 1} of ${steps.length}`}>
      {steps.map((step, index) => (
        <li key={step} className={index <= currentIndex ? styles.progressActive : undefined}>
          <span>{index + 1}</span>
          {labels[step]}
        </li>
      ))}
    </ol>
  );
}

Progress.propTypes = {
  currentStep: PropTypes.string.isRequired,
  steps: PropTypes.arrayOf(PropTypes.string).isRequired,
  labels: PropTypes.objectOf(PropTypes.string).isRequired
};

function BrandSelect({ id, label, options, initialValue, onChange }) {
  const initialIndex = Math.max(
    options.findIndex(option => option.value === initialValue),
    0
  );
  const [selectedIndex, setSelectedIndex] = useState(initialIndex);
  const [activeIndex, setActiveIndex] = useState(initialIndex);
  const [open, setOpen] = useState(false);
  const [menuAbove, setMenuAbove] = useState(false);
  const rootRef = useRef(null);
  const triggerRef = useRef(null);
  const optionRefs = useRef([]);
  const listboxId = `${id}-listbox`;
  const labelId = `${id}-label`;

  useEffect(() => {
    if (!open) return undefined;

    const handleOutsidePointer = event => {
      if (!rootRef.current?.contains(event.target)) setOpen(false);
    };

    document.addEventListener("pointerdown", handleOutsidePointer);
    return () => document.removeEventListener("pointerdown", handleOutsidePointer);
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const animationFrame = window.requestAnimationFrame(() => optionRefs.current[activeIndex]?.focus());
    return () => window.cancelAnimationFrame(animationFrame);
  }, [activeIndex, open]);

  const openMenu = index => {
    const triggerBounds = triggerRef.current?.getBoundingClientRect();
    const estimatedMenuHeight = Math.min(options.length * 48 + 12, 224);
    if (triggerBounds) {
      const spaceBelow = window.innerHeight - triggerBounds.bottom;
      const spaceAbove = triggerBounds.top;
      setMenuAbove(spaceBelow < estimatedMenuHeight + 12 && spaceAbove > spaceBelow);
    }
    setActiveIndex(index);
    setOpen(true);
  };

  const closeMenu = returnFocus => {
    setOpen(false);
    if (returnFocus) window.requestAnimationFrame(() => triggerRef.current?.focus());
  };

  const selectOption = index => {
    setSelectedIndex(index);
    onChange?.(options[index].value);
    closeMenu(true);
  };

  const handleTriggerKeyDown = event => {
    if (["ArrowDown", "ArrowUp", "Home", "End"].includes(event.key)) {
      event.preventDefault();
      if (event.key === "End") openMenu(options.length - 1);
      else if (event.key === "Home") openMenu(0);
      else if (event.key === "ArrowUp") openMenu((selectedIndex - 1 + options.length) % options.length);
      else openMenu((selectedIndex + 1) % options.length);
    }
  };

  const handleOptionKeyDown = (event, index) => {
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      const direction = event.key === "ArrowDown" ? 1 : -1;
      setActiveIndex((index + direction + options.length) % options.length);
    } else if (event.key === "Home" || event.key === "End") {
      event.preventDefault();
      setActiveIndex(event.key === "Home" ? 0 : options.length - 1);
    } else if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      selectOption(index);
    } else if (event.key === "Escape") {
      event.preventDefault();
      closeMenu(true);
    } else if (event.key === "Tab") {
      setOpen(false);
    }
  };

  return (
    <div className={styles.field}>
      <span id={labelId}>{label}</span>
      <div className={styles.brandSelect} ref={rootRef}>
        <button
          className={`${styles.selectTrigger} ${open ? styles.selectTriggerOpen : ""}`}
          id={id}
          ref={triggerRef}
          type="button"
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-controls={listboxId}
          aria-labelledby={`${labelId} ${id}`}
          onClick={() => (open ? closeMenu(false) : openMenu(selectedIndex))}
          onKeyDown={handleTriggerKeyDown}
        >
          <span>{options[selectedIndex].label}</span>
          <FontAwesomeIcon icon={faChevronDown} aria-hidden="true" />
        </button>
        {open && (
          <ul
            className={`${styles.selectMenu} ${menuAbove ? styles.selectMenuAbove : ""}`}
            id={listboxId}
            role="listbox"
            aria-labelledby={labelId}
          >
            {options.map((option, index) => (
              <li
                key={option.value}
                ref={element => {
                  optionRefs.current[index] = element;
                }}
                role="option"
                aria-selected={selectedIndex === index}
                tabIndex={-1}
                onClick={() => selectOption(index)}
                onKeyDown={event => handleOptionKeyDown(event, index)}
              >
                <span>{option.label}</span>
                {selectedIndex === index && <FontAwesomeIcon icon={faCheck} aria-hidden="true" />}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

BrandSelect.propTypes = {
  id: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired
    })
  ).isRequired,
  initialValue: PropTypes.string.isRequired,
  onChange: PropTypes.func
};

function SetupShell({ step, title, description, children, onBack, flow = "attendee" }) {
  const isOrganizer = flow === "organizer";
  const steps = isOrganizer ? ORGANIZER_STEPS : ATTENDEE_STEPS;
  const labels = isOrganizer ? ORGANIZER_LABELS : ATTENDEE_LABELS;
  return (
    <div className={styles.setupPage}>
      <DemoHeader onExit={() => window.location.assign("/")} />
      <main className={styles.setupMain}>
        <section
          className={`${styles.setupCard} ${isOrganizer ? styles.organizerCard : ""}`}
          aria-labelledby="demo-step-title"
        >
          <div className={styles.setupCardHeader}>
            {onBack ? (
              <button className={styles.backButton} type="button" onClick={onBack}>
                <FontAwesomeIcon icon={faArrowLeft} />
                Back
              </button>
            ) : (
              <span />
            )}
            <span>{isOrganizer ? "Create immersive event" : "i3 Immersive Meetup"}</span>
            <span className={styles.previewBadge}>Demo</span>
          </div>
          <div className={styles.setupContent}>
            <Progress currentStep={step} steps={steps} labels={labels} />
            <div className={styles.stepHeading}>
              <span>{isOrganizer ? "Organizer workspace" : "Event setup"}</span>
              <h1 id="demo-step-title" tabIndex="-1">
                {title}
              </h1>
              <p>{description}</p>
            </div>
            {children}
          </div>
        </section>
      </main>
    </div>
  );
}

SetupShell.propTypes = {
  step: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
  onBack: PropTypes.func,
  flow: PropTypes.oneOf(["attendee", "organizer"])
};

function DeviceStep({ onNext }) {
  const [device, setDevice] = useState("desktop");
  return (
    <div className={styles.stepBody}>
      <div className={styles.choiceGrid} role="radiogroup" aria-label="Choose how to join">
        <button
          className={device === "desktop" ? styles.choiceSelected : styles.choice}
          type="button"
          role="radio"
          aria-checked={device === "desktop"}
          onClick={() => setDevice("desktop")}
        >
          <span className={styles.choiceIcon} aria-hidden="true">
            <FontAwesomeIcon icon={faDesktop} />
          </span>
          <span className={styles.choiceCopy}>
            <strong>This device</strong>
            <small>Use this browser on desktop, tablet or mobile</small>
          </span>
        </button>
        <button
          className={device === "vr" ? styles.choiceSelected : styles.choice}
          type="button"
          role="radio"
          aria-checked={device === "vr"}
          onClick={() => setDevice("vr")}
        >
          <span className={styles.choiceIcon} aria-hidden="true">
            <FontAwesomeIcon icon={faVrCardboard} />
          </span>
          <span className={styles.choiceCopy}>
            <strong>VR headset</strong>
            <small>Pair a headset with a secure one-time code</small>
          </span>
        </button>
      </div>
      <button className={styles.primaryButton} type="button" onClick={onNext}>
        Continue to audio
      </button>
      <button className={styles.textButton} type="button" onClick={onNext}>
        Watch without joining
      </button>
    </div>
  );
}

DeviceStep.propTypes = {
  onNext: PropTypes.func.isRequired
};

function AudioStep({ onBack, onNext }) {
  const [muted, setMuted] = useState(false);
  return (
    <div className={styles.stepBody}>
      <div className={styles.audioGrid}>
        <BrandSelect
          id="demo-microphone"
          label="Microphone"
          options={MICROPHONE_OPTIONS}
          initialValue="default-microphone"
        />
        <div className={styles.levelRow}>
          <FontAwesomeIcon icon={faMicrophone} />
          <span
            className={muted ? styles.levelMuted : styles.level}
            aria-label={muted ? "Microphone muted" : "Microphone level active"}
          >
            <i />
          </span>
          <button
            className={muted ? styles.switchOff : styles.switchOn}
            type="button"
            role="switch"
            aria-checked={!muted}
            onClick={() => setMuted(value => !value)}
          >
            <span />
            {muted ? "Unmute" : "Mute"}
          </button>
        </div>
        <BrandSelect id="demo-speakers" label="Speakers" options={SPEAKER_OPTIONS} initialValue="default-speakers" />
        <div className={styles.levelRow}>
          <FontAwesomeIcon icon={faVolumeUp} />
          <span className={styles.level} aria-label="Speaker test level">
            <i />
          </span>
          <button className={styles.secondaryButtonSmall} type="button">
            Test audio
          </button>
        </div>
      </div>
      <div className={styles.actionRow}>
        <button className={styles.secondaryButton} type="button" onClick={onBack}>
          Back
        </button>
        <button className={styles.primaryButton} type="button" onClick={onNext}>
          Continue to avatar
        </button>
      </div>
    </div>
  );
}

AudioStep.propTypes = {
  onBack: PropTypes.func.isRequired,
  onNext: PropTypes.func.isRequired
};

function AvatarStep({ onBack, onNext }) {
  const [displayName, setDisplayName] = useState("i3 Guest");
  return (
    <div className={styles.stepBody}>
      <div className={styles.avatarLayout}>
        <div className={styles.avatarForm}>
          <label className={styles.field}>
            <span>Display name</span>
            <input value={displayName} onChange={event => setDisplayName(event.target.value)} maxLength={32} />
            <small>This is how other attendees will see you.</small>
          </label>
          <label className={styles.field}>
            <span>
              Pronouns <em>Optional</em>
            </span>
            <input placeholder="For example, she/her" />
          </label>
          <button className={styles.secondaryButton} type="button">
            Choose another avatar
          </button>
        </div>
        <div className={styles.avatarPreview} aria-label="Preview of the selected i3 avatar">
          <div className={styles.avatarHead}>
            <i />
            <i />
          </div>
          <div className={styles.avatarBody} />
          <span>i3 Standard</span>
        </div>
      </div>
      <div className={styles.actionRow}>
        <button className={styles.secondaryButton} type="button" onClick={onBack}>
          Back
        </button>
        <button className={styles.primaryButton} type="button" onClick={onNext} disabled={!displayName.trim()}>
          Enter event space
        </button>
      </div>
    </div>
  );
}

AvatarStep.propTypes = {
  onBack: PropTypes.func.isRequired,
  onNext: PropTypes.func.isRequired
};

const eventDraftType = PropTypes.shape({
  title: PropTypes.string.isRequired,
  date: PropTypes.string.isRequired,
  time: PropTypes.string.isRequired,
  duration: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  coOrganizers: PropTypes.string.isRequired,
  attendees: PropTypes.string.isRequired,
  sensitivity: PropTypes.string.isRequired,
  space: PropTypes.string.isRequired,
  access: PropTypes.string.isRequired
});

function EventDetailsStep({ draft, updateDraft, onNext }) {
  return (
    <form
      className={styles.stepBody}
      onSubmit={event => {
        event.preventDefault();
        onNext();
      }}
    >
      <div className={styles.formGrid}>
        <label className={`${styles.field} ${styles.fieldWide}`}>
          <span>Event name</span>
          <input
            value={draft.title}
            onChange={event => updateDraft({ title: event.target.value })}
            required
            maxLength={80}
          />
        </label>
        <label className={styles.field}>
          <span>Date</span>
          <input
            type="date"
            value={draft.date}
            onChange={event => updateDraft({ date: event.target.value })}
            required
          />
        </label>
        <label className={styles.field}>
          <span>Start time</span>
          <input
            type="time"
            value={draft.time}
            onChange={event => updateDraft({ time: event.target.value })}
            required
          />
        </label>
        <BrandSelect
          id="demo-duration"
          label="Duration"
          options={[
            { value: "30", label: "30 minutes" },
            { value: "60", label: "1 hour" },
            { value: "90", label: "1 hour 30 minutes" }
          ]}
          initialValue={draft.duration}
          onChange={duration => updateDraft({ duration })}
        />
        <label className={`${styles.field} ${styles.fieldWide}`}>
          <span>Description</span>
          <textarea
            value={draft.description}
            onChange={event => updateDraft({ description: event.target.value })}
            rows="3"
          />
          <small>Tell attendees what to expect and how to prepare.</small>
        </label>
        <label className={styles.field}>
          <span>
            Co-organizers <em>Optional</em>
          </span>
          <input
            type="email"
            value={draft.coOrganizers}
            onChange={event => updateDraft({ coOrganizers: event.target.value })}
            placeholder="name@i3simulations.com"
          />
          <small>Co-organizers can customize and host.</small>
        </label>
        <label className={styles.field}>
          <span>Invite attendees</span>
          <input
            value={draft.attendees}
            onChange={event => updateDraft({ attendees: event.target.value })}
            placeholder="Email or group"
          />
          <small>You can share a guest link after publishing.</small>
        </label>
      </div>
      <button className={styles.primaryButton} type="submit">
        Choose a space
      </button>
    </form>
  );
}

EventDetailsStep.propTypes = {
  draft: eventDraftType.isRequired,
  updateDraft: PropTypes.func.isRequired,
  onNext: PropTypes.func.isRequired
};

function EventSpaceStep({ draft, updateDraft, onBack, onNext }) {
  const spaces = [
    {
      id: "i3-atrium",
      name: "i3 Immersive Atrium",
      detail: "Branded stage, networking zones and presentation screens"
    },
    { id: "i3-studio", name: "i3 Collaboration Studio", detail: "Compact workshop layout for smaller groups" },
    { id: "custom-glb", name: "Custom GLB space", detail: "Prepare a Blender scene for the Hubs scene editor" }
  ];
  return (
    <div className={styles.stepBody}>
      <div className={styles.spaceGrid} role="radiogroup" aria-label="Choose an immersive space">
        {spaces.map((space, index) => (
          <button
            key={space.id}
            className={draft.space === space.id ? styles.spaceCardSelected : styles.spaceCard}
            type="button"
            role="radio"
            aria-checked={draft.space === space.id}
            onClick={() => updateDraft({ space: space.id })}
          >
            <span className={styles.spacePreview}>
              {index === 0 ? (
                <img src={immersiveMeetupPoster} alt="i3 Immersive Atrium preview" />
              ) : (
                <FontAwesomeIcon icon={index === 1 ? faUsers : faCog} />
              )}
            </span>
            <span>
              <strong>{space.name}</strong>
              <small>{space.detail}</small>
            </span>
            {draft.space === space.id && <FontAwesomeIcon icon={faCheck} aria-hidden="true" />}
          </button>
        ))}
      </div>
      <div className={styles.infoStrip}>
        <FontAwesomeIcon icon={faCog} aria-hidden="true" />
        <span>
          <strong>Customize after publishing</strong>
          Add logos, videos, images and GLB models in the i3 scene editor before the event starts.
        </span>
      </div>
      <div className={styles.actionRow}>
        <button className={styles.secondaryButton} type="button" onClick={onBack}>
          Back
        </button>
        <button className={styles.primaryButton} type="button" onClick={onNext}>
          Set event access
        </button>
      </div>
    </div>
  );
}

EventSpaceStep.propTypes = {
  draft: eventDraftType.isRequired,
  updateDraft: PropTypes.func.isRequired,
  onBack: PropTypes.func.isRequired,
  onNext: PropTypes.func.isRequired
};

function EventAccessStep({ draft, updateDraft, onBack, onNext }) {
  const accessOptions = [
    { id: "invite", title: "Invited guests", detail: "Only people with an invitation can enter" },
    { id: "link", title: "Anyone with the link", detail: "Guests can join using the shared event link" },
    { id: "public", title: "Public listing", detail: "Show this event on the i3 Meet home page" }
  ];
  return (
    <div className={styles.stepBody}>
      <div className={styles.accessLayout}>
        <div className={styles.accessColumn}>
          <div className={styles.accessOptions} role="radiogroup" aria-label="Event access">
            {accessOptions.map(option => (
              <button
                key={option.id}
                className={draft.access === option.id ? styles.accessSelected : styles.accessOption}
                type="button"
                role="radio"
                aria-checked={draft.access === option.id}
                onClick={() => updateDraft({ access: option.id })}
              >
                <i aria-hidden="true" />
                <span>
                  <strong>{option.title}</strong>
                  <small>{option.detail}</small>
                </span>
              </button>
            ))}
          </div>
          <BrandSelect
            id="demo-sensitivity"
            label="Sensitivity"
            options={SENSITIVITY_OPTIONS}
            initialValue={draft.sensitivity}
            onChange={sensitivity => updateDraft({ sensitivity })}
          />
        </div>
        <div className={styles.permissionCard}>
          <span>Attendee permissions</span>
          <SettingRow label="Voice chat" initiallyEnabled />
          <SettingRow label="Text chat" initiallyEnabled />
          <SettingRow label="Create objects" initiallyEnabled={false} />
        </div>
      </div>
      <div className={styles.actionRow}>
        <button className={styles.secondaryButton} type="button" onClick={onBack}>
          Back
        </button>
        <button className={styles.primaryButton} type="button" onClick={onNext}>
          Review event
        </button>
      </div>
    </div>
  );
}

EventAccessStep.propTypes = {
  draft: eventDraftType.isRequired,
  updateDraft: PropTypes.func.isRequired,
  onBack: PropTypes.func.isRequired,
  onNext: PropTypes.func.isRequired
};

function EventReviewStep({ draft, onBack, onPublish }) {
  return (
    <div className={styles.stepBody}>
      <div className={styles.reviewLayout}>
        <div className={styles.reviewPoster}>
          <img src={immersiveMeetupPoster} alt="Selected i3 immersive event space" />
          <span>i3 Immersive Atrium</span>
        </div>
        <dl className={styles.reviewList}>
          <div>
            <dt>Event</dt>
            <dd>{draft.title}</dd>
          </div>
          <div>
            <dt>Schedule</dt>
            <dd>
              {formatEventDate(draft.date)} at {draft.time}, {draft.duration} minutes
            </dd>
          </div>
          <div>
            <dt>Access</dt>
            <dd>
              {draft.access === "invite"
                ? "Invited guests"
                : draft.access === "link"
                  ? "Anyone with the link"
                  : "Public listing"}
            </dd>
          </div>
          <div>
            <dt>Readiness</dt>
            <dd>
              <FontAwesomeIcon icon={faCheck} /> Required details complete
            </dd>
          </div>
        </dl>
      </div>
      <div className={styles.infoStrip}>
        <FontAwesomeIcon icon={faCheck} aria-hidden="true" />
        <span>
          <strong>Ready to publish</strong>You can customize the 3D space and copy the guest link from the event
          dashboard.
        </span>
      </div>
      <div className={styles.actionRow}>
        <button className={styles.secondaryButton} type="button" onClick={onBack}>
          Back
        </button>
        <button className={styles.primaryButton} type="button" onClick={onPublish}>
          Publish event
        </button>
      </div>
    </div>
  );
}

EventReviewStep.propTypes = {
  draft: eventDraftType.isRequired,
  onBack: PropTypes.func.isRequired,
  onPublish: PropTypes.func.isRequired
};

function SettingRow({ label, initiallyEnabled }) {
  const [enabled, setEnabled] = useState(initiallyEnabled);
  return (
    <button
      className={styles.settingRow}
      type="button"
      role="switch"
      aria-checked={enabled}
      onClick={() => setEnabled(value => !value)}
    >
      <span>
        <strong>{label}</strong>
        <small>{enabled ? "Allowed for attendees" : "Host only"}</small>
      </span>
      <i className={enabled ? styles.miniSwitchOn : styles.miniSwitchOff} />
    </button>
  );
}

SettingRow.propTypes = {
  label: PropTypes.string.isRequired,
  initiallyEnabled: PropTypes.bool.isRequired
};

function EventDashboardStep({ draft, customized, onEdit, onCustomize, onEnterHost, onJoinGuest, onExit }) {
  const [copied, setCopied] = useState(false);
  return (
    <div className={styles.dashboardPage}>
      <DemoHeader onExit={onExit} />
      <main className={styles.dashboardMain}>
        <div className={styles.dashboardHeader}>
          <div>
            <span className={styles.statusBadge}>
              <i /> Scheduled
            </span>
            <h1 tabIndex="-1">{draft.title}</h1>
            <p>
              {formatEventDate(draft.date)} at {draft.time} · {draft.duration} minutes
            </p>
          </div>
          <button className={styles.secondaryButton} type="button" onClick={onEdit}>
            Edit event
          </button>
        </div>
        <div className={styles.dashboardGrid}>
          <section className={styles.eventOverview} aria-labelledby="event-overview-title">
            <div className={styles.dashboardSectionHeader}>
              <div>
                <span>Event overview</span>
                <h2 id="event-overview-title">Prepare and share</h2>
              </div>
            </div>
            <div className={styles.overviewHero}>
              <img src={immersiveMeetupPoster} alt="i3 Immersive Atrium" />
              <div>
                <strong>i3 Immersive Atrium</strong>
                <small>Branded stage and networking zones</small>
              </div>
            </div>
            <div className={styles.linkCard}>
              <span>
                <strong>Guest link</strong>
                <small>meet.i3simulations.com/events/i3-meetup</small>
              </span>
              <button
                className={styles.secondaryButtonSmall}
                type="button"
                onClick={() => {
                  setCopied(true);
                  window.setTimeout(() => setCopied(false), 2400);
                }}
              >
                {copied ? "Copied" : "Copy link"}
              </button>
            </div>
            <div className={styles.dashboardActions}>
              <button className={styles.primaryButton} type="button" onClick={onEnterHost}>
                Enter host room
              </button>
              <button className={styles.secondaryButton} type="button" onClick={onCustomize}>
                Customize 3D space
              </button>
              <button className={styles.secondaryButton} type="button" onClick={onJoinGuest}>
                Preview guest journey
              </button>
            </div>
          </section>
          <aside className={styles.readinessCard} aria-labelledby="readiness-title">
            <div className={styles.dashboardSectionHeader}>
              <div>
                <span>Organizer checklist</span>
                <h2 id="readiness-title">Event readiness</h2>
              </div>
              <strong>{customized ? "4 of 4" : "3 of 4"}</strong>
            </div>
            <ul>
              <li className={styles.checkComplete}>
                <FontAwesomeIcon icon={faCheck} />
                <span>
                  <strong>Event details</strong>
                  <small>Date, time and access confirmed</small>
                </span>
              </li>
              <li className={styles.checkComplete}>
                <FontAwesomeIcon icon={faCheck} />
                <span>
                  <strong>Space selected</strong>
                  <small>i3 Immersive Atrium</small>
                </span>
              </li>
              <li className={styles.checkComplete}>
                <FontAwesomeIcon icon={faCheck} />
                <span>
                  <strong>Guest link ready</strong>
                  <small>Share with invited attendees</small>
                </span>
              </li>
              <li className={customized ? styles.checkComplete : undefined}>
                <FontAwesomeIcon icon={customized ? faCheck : faCog} />
                <span>
                  <strong>Customize space</strong>
                  <small>
                    {customized ? "Placeholder experience saved" : "Add GLB models, signage and presentation media"}
                  </small>
                </span>
              </li>
            </ul>
            <button className={styles.secondaryButton} type="button" onClick={onCustomize}>
              {customized ? "Edit customization" : "Open customization studio"}
            </button>
            <p>
              This local demo shows the intended workflow. Publishing and scheduling connect when deployed to the shared
              Hubs server.
            </p>
          </aside>
        </div>
      </main>
    </div>
  );
}

EventDashboardStep.propTypes = {
  draft: eventDraftType.isRequired,
  customized: PropTypes.bool.isRequired,
  onEdit: PropTypes.func.isRequired,
  onCustomize: PropTypes.func.isRequired,
  onEnterHost: PropTypes.func.isRequired,
  onJoinGuest: PropTypes.func.isRequired,
  onExit: PropTypes.func.isRequired
};

function SpaceEditorStep({ onBack, onSave, onPreview }) {
  const [activeObject, setActiveObject] = useState("Welcome screen");
  const [welcomeVisible, setWelcomeVisible] = useState(true);
  const [modelVisible, setModelVisible] = useState(true);
  const [saved, setSaved] = useState(false);
  const tools = [
    { name: "Welcome screen", detail: "Branded image and event message", icon: faDesktop },
    { name: "Presentation screen", detail: "Host screenshare surface", icon: faShareSquare },
    { name: "Product model", detail: "Placeholder for your GLB asset", icon: faCog },
    { name: "Networking zone", detail: "Conversation and seating area", icon: faUsers }
  ];

  const handleSave = () => {
    setSaved(true);
    onSave();
  };

  return (
    <div className={styles.studioPage}>
      <DemoHeader onExit={() => window.location.assign("/")} />
      <main className={styles.studioMain}>
        <header className={styles.studioHeader}>
          <div>
            <button className={styles.backButton} type="button" onClick={onBack}>
              <FontAwesomeIcon icon={faArrowLeft} />
              Event dashboard
            </button>
            <span>Experience preparation</span>
            <h1 tabIndex="-1">Customize i3 Immersive Atrium</h1>
            <p>Prepare the branded space, presentation surfaces and interaction points before guests arrive.</p>
          </div>
          <div className={styles.studioActions}>
            <button className={styles.secondaryButton} type="button" onClick={onPreview}>
              Preview as attendee
            </button>
            <button className={styles.primaryButton} type="button" onClick={handleSave}>
              {saved ? "Changes saved" : "Save customization"}
            </button>
          </div>
        </header>

        <div className={styles.studioWorkspace}>
          <aside className={styles.assetPanel} aria-labelledby="asset-panel-title">
            <div className={styles.panelTitle}>
              <span>Scene content</span>
              <h2 id="asset-panel-title">Objects</h2>
            </div>
            <div className={styles.assetList}>
              {tools.map(tool => (
                <button
                  key={tool.name}
                  className={activeObject === tool.name ? styles.assetSelected : styles.assetButton}
                  type="button"
                  aria-pressed={activeObject === tool.name}
                  onClick={() => setActiveObject(tool.name)}
                >
                  <FontAwesomeIcon icon={tool.icon} />
                  <span>
                    <strong>{tool.name}</strong>
                    <small>{tool.detail}</small>
                  </span>
                </button>
              ))}
            </div>
            <button className={styles.secondaryButton} type="button" onClick={() => setActiveObject("Uploaded GLB")}>
              Add GLB model
            </button>
          </aside>

          <section className={styles.scenePreview} aria-label="Placeholder preview of i3 Immersive Atrium">
            <img src={immersiveMeetupPoster} alt="Placeholder i3 Immersive Atrium environment" />
            <div className={styles.sceneTopbar}>
              <span>
                <i /> Draft saved locally
              </span>
              <strong>Static scene preview</strong>
            </div>
          </section>

          <aside className={styles.inspectorPanel} aria-labelledby="inspector-title">
            <div className={styles.panelTitle}>
              <span>Properties</span>
              <h2 id="inspector-title">{activeObject}</h2>
            </div>
            <label className={styles.field}>
              <span>Display name</span>
              <input value={activeObject} onChange={event => setActiveObject(event.target.value)} />
            </label>
            <div className={styles.inspectorSection}>
              <span>Visibility</span>
              <button
                className={styles.settingRow}
                type="button"
                role="switch"
                aria-checked={activeObject === "Product model" ? modelVisible : welcomeVisible}
                onClick={() =>
                  activeObject === "Product model"
                    ? setModelVisible(value => !value)
                    : setWelcomeVisible(value => !value)
                }
              >
                <span>
                  <strong>Show when event opens</strong>
                  <small>This setting will apply when the object is connected to Hubs</small>
                </span>
                <i
                  className={
                    activeObject === "Product model"
                      ? modelVisible
                        ? styles.miniSwitchOn
                        : styles.miniSwitchOff
                      : welcomeVisible
                        ? styles.miniSwitchOn
                        : styles.miniSwitchOff
                  }
                />
              </button>
            </div>
            <div className={styles.infoStrip}>
              <FontAwesomeIcon icon={faCog} aria-hidden="true" />
              <span>
                <strong>GLB-ready placeholder</strong>
                Replace this visual with your optimized Blender export when the production scene is ready.
              </span>
            </div>
          </aside>
        </div>
      </main>
    </div>
  );
}

SpaceEditorStep.propTypes = {
  onBack: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  onPreview: PropTypes.func.isRequired
};

function RoomPanel({
  panel,
  onClose,
  eventLive,
  onToggleEvent,
  lobbyAdmitted,
  onAdmitLobby,
  currentRoom,
  onRoomChange
}) {
  if (!panel) return null;
  const isHostPanel = panel === "host";
  return (
    <aside
      className={styles.roomPanel}
      aria-label={
        isHostPanel
          ? "Host controls"
          : panel === "settings"
            ? "Room settings"
            : panel === "people"
              ? "People and lobby"
              : panel === "help"
                ? "Movement help"
                : "Event chat"
      }
    >
      <div className={styles.panelHeader}>
        <div>
          <span>Demo controls</span>
          <h2>
            {isHostPanel
              ? "Host controls"
              : panel === "settings"
                ? "Room settings"
                : panel === "people"
                  ? "People and lobby"
                  : panel === "help"
                    ? "Movement help"
                    : "Event chat"}
          </h2>
        </div>
        <button type="button" onClick={onClose} aria-label="Close panel">
          ×
        </button>
      </div>
      {isHostPanel ? (
        <div className={styles.settingsList}>
          <div className={styles.hostStatus} role="status">
            <i />
            <span>
              <strong>{eventLive ? "Event is live" : "Event not started"}</strong>
              <small>
                {eventLive ? "The host is broadcasting to attendee rooms" : "Attendees are waiting in the lobby"}
              </small>
            </span>
          </div>
          <button className={styles.primaryButton} type="button" onClick={onToggleEvent}>
            {eventLive ? "End event" : "Start event"}
          </button>
          <SettingRow label="Attendee audio" initiallyEnabled />
          <SettingRow label="Hand raises" initiallyEnabled />
          <SettingRow label="Seat attendees" initiallyEnabled={false} />
          <BrandSelect
            id="demo-event-room"
            label="Current event room"
            options={[
              { value: "host", label: "Host room" },
              { value: "attendee-1", label: "Attendee room 1" },
              { value: "attendee-2", label: "Attendee room 2" }
            ]}
            initialValue={currentRoom}
            onChange={onRoomChange}
          />
          <div className={styles.lobbyCard}>
            <span>
              <strong>{lobbyAdmitted ? "Lobby clear" : "1 guest waiting"}</strong>
              <small>{lobbyAdmitted ? "All invited guests are admitted" : "Taylor Morgan requested access"}</small>
            </span>
            {!lobbyAdmitted && (
              <button className={styles.secondaryButtonSmall} type="button" onClick={onAdmitLobby}>
                Admit
              </button>
            )}
          </div>
        </div>
      ) : panel === "settings" ? (
        <div className={styles.settingsList}>
          <label className={styles.field}>
            <span>Room name</span>
            <input defaultValue="i3 Immersive Meetup" />
          </label>
          <BrandSelect id="demo-capacity" label="Room capacity" options={CAPACITY_OPTIONS} initialValue="24" />
          {["Voice chat", "Text chat", "Reactions", "Create and move objects"].map((setting, index) => (
            <SettingRow key={setting} label={setting} initiallyEnabled={index < 3} />
          ))}
          <button className={styles.primaryButton} type="button" onClick={onClose}>
            Save settings
          </button>
        </div>
      ) : panel === "people" ? (
        <div className={styles.settingsList}>
          <div className={styles.peopleSummary}>
            <strong>3 in this room</strong>
            <small>Spatial audio connects people nearby.</small>
          </div>
          {["i3 Host", "Taylor Morgan", "You"].map((person, index) => (
            <div className={styles.personRow} key={person}>
              <i>{person.charAt(0)}</i>
              <span>
                <strong>{person}</strong>
                <small>{index === 0 ? "Organizer" : index === 1 ? "Hand raised" : "Attendee"}</small>
              </span>
              {index === 1 && <span className={styles.statusBadge}>Q&amp;A</span>}
            </div>
          ))}
        </div>
      ) : panel === "help" ? (
        <div className={styles.settingsList}>
          <div className={styles.tutorialGrid}>
            <span>
              <strong>Move</strong>
              <small>Use W, A, S and D or the arrow keys.</small>
            </span>
            <span>
              <strong>Look around</strong>
              <small>Drag with your mouse or trackpad.</small>
            </span>
            <span>
              <strong>Talk naturally</strong>
              <small>Move closer to hear people more clearly.</small>
            </span>
          </div>
          <button className={styles.primaryButton} type="button" onClick={onClose}>
            Got it
          </button>
        </div>
      ) : (
        <div className={styles.chatBody}>
          <div>
            <strong>i3 Host</strong>
            <p>Welcome to the immersive meetup.</p>
          </div>
          <div>
            <strong>You</strong>
            <p>Excited to explore the space.</p>
          </div>
          <label className={styles.field}>
            <span>Message</span>
            <input placeholder="Type a message" />
          </label>
        </div>
      )}
    </aside>
  );
}

RoomPanel.propTypes = {
  panel: PropTypes.oneOf(["settings", "chat", "host", "people", "help"]),
  onClose: PropTypes.func.isRequired,
  eventLive: PropTypes.bool.isRequired,
  onToggleEvent: PropTypes.func.isRequired,
  lobbyAdmitted: PropTypes.bool.isRequired,
  onAdmitLobby: PropTypes.func.isRequired,
  currentRoom: PropTypes.string.isRequired,
  onRoomChange: PropTypes.func.isRequired
};

function RoomStep({ onExit, hostMode = false }) {
  const [panel, setPanel] = useState(null);
  const [micOn, setMicOn] = useState(true);
  const [reaction, setReaction] = useState(false);
  const [eventLive, setEventLive] = useState(false);
  const [sharing, setSharing] = useState(false);
  const [lobbyAdmitted, setLobbyAdmitted] = useState(false);
  const [currentRoom, setCurrentRoom] = useState("host");
  return (
    <div className={styles.roomPage}>
      <DemoHeader onExit={onExit} compact />
      <main className={styles.roomStage}>
        <img className={styles.roomBackground} src={immersiveMeetupPoster} alt="" aria-hidden="true" />
        <div className={styles.roomShade} />
        <div className={styles.roomStatus}>
          <span>{hostMode ? (eventLive ? "Event live" : "Host room") : "Demo mode"}</span>
          <h1 tabIndex="-1">i3 Immersive Meetup</h1>
          <p>
            {hostMode
              ? eventLive
                ? "You are broadcasting across attendee rooms"
                : "Prepare the event before attendees enter"
              : "You are inside the shared event space"}
          </p>
        </div>
        <div className={styles.peopleCard}>
          <FontAwesomeIcon icon={faUsers} />
          <span>
            <strong>3</strong> people
          </span>
        </div>
        {reaction && (
          <div className={styles.reaction} role="status">
            Great idea!
          </div>
        )}
        {sharing && (
          <section className={styles.presentationCard} aria-label="Shared presentation preview">
            <span>Live presentation</span>
            <h2>i3 product showcase</h2>
            <p>The host is sharing content with everyone in the event.</p>
          </section>
        )}
        <nav className={styles.roomToolbar} aria-label="Meeting controls">
          {hostMode && (
            <button
              type="button"
              aria-pressed={panel === "host"}
              onClick={() => setPanel(panel === "host" ? null : "host")}
            >
              <span>
                <FontAwesomeIcon icon={faUsers} />
              </span>
              Host
            </button>
          )}
          <button type="button" aria-pressed={micOn} onClick={() => setMicOn(value => !value)}>
            <span>
              <FontAwesomeIcon icon={faMicrophone} />
            </span>
            {micOn ? "Mic on" : "Muted"}
          </button>
          <button type="button" aria-pressed={sharing} onClick={() => setSharing(value => !value)}>
            <span>
              <FontAwesomeIcon icon={faShareSquare} />
            </span>
            {sharing ? "Stop share" : "Share"}
          </button>
          <button type="button" aria-pressed={reaction} onClick={() => setReaction(value => !value)}>
            <span>
              <FontAwesomeIcon icon={faSmile} />
            </span>
            React
          </button>
          <button
            type="button"
            aria-pressed={panel === "chat"}
            onClick={() => setPanel(panel === "chat" ? null : "chat")}
          >
            <span>
              <FontAwesomeIcon icon={faComment} />
            </span>
            Chat
          </button>
          <button
            type="button"
            aria-pressed={panel === "settings"}
            onClick={() => setPanel(panel === "settings" ? null : "settings")}
          >
            <span>
              <FontAwesomeIcon icon={faCog} />
            </span>
            Settings
          </button>
          <button
            type="button"
            aria-pressed={panel === "people"}
            onClick={() => setPanel(panel === "people" ? null : "people")}
          >
            <span>
              <FontAwesomeIcon icon={faUsers} />
            </span>
            People
          </button>
          <button
            type="button"
            aria-pressed={panel === "help"}
            onClick={() => setPanel(panel === "help" ? null : "help")}
          >
            <span>
              <FontAwesomeIcon icon={faQuestionCircle} />
            </span>
            Help
          </button>
        </nav>
        <RoomPanel
          panel={panel}
          onClose={() => setPanel(null)}
          eventLive={eventLive}
          onToggleEvent={() => setEventLive(value => !value)}
          lobbyAdmitted={lobbyAdmitted}
          onAdmitLobby={() => setLobbyAdmitted(true)}
          currentRoom={currentRoom}
          onRoomChange={setCurrentRoom}
        />
      </main>
    </div>
  );
}

RoomStep.propTypes = {
  onExit: PropTypes.func.isRequired,
  hostMode: PropTypes.bool
};

export function PreviewExperience() {
  const [step, setStep] = useState(getStepFromUrl);
  const [eventDraft, setEventDraft] = useState(getInitialEventDraft);
  const [customized, setCustomized] = useState(
    () => window.sessionStorage.getItem("i3-meet-space-customized") === "true"
  );

  useEffect(() => {
    const updateFromHistory = () => setStep(getStepFromUrl());
    window.addEventListener("popstate", updateFromHistory);
    return () => window.removeEventListener("popstate", updateFromHistory);
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
    window.requestAnimationFrame(() => document.querySelector("h1")?.focus({ preventScroll: true }));
  }, [step]);

  useEffect(() => {
    window.sessionStorage.setItem("i3-meet-event-draft", JSON.stringify(eventDraft));
  }, [eventDraft]);

  const goTo = (nextStep, options = {}) => {
    const nextUrl = new URL(window.location.href);
    nextUrl.searchParams.set("demo", nextStep);
    if (options.role) nextUrl.searchParams.set("role", options.role);
    else nextUrl.searchParams.delete("role");
    window.history.pushState({}, "", nextUrl);
    setStep(nextStep);
    window.scrollTo(0, 0);
  };

  const updateDraft = changes => setEventDraft(current => ({ ...current, ...changes }));
  const isHostMode = new URLSearchParams(window.location.search).get("role") === "host";

  if (step === "room") {
    return <RoomStep onExit={() => window.location.assign("/")} hostMode={isHostMode} />;
  }
  if (step === "space-editor") {
    return (
      <SpaceEditorStep
        onBack={() => goTo("event-dashboard")}
        onSave={() => {
          setCustomized(true);
          window.sessionStorage.setItem("i3-meet-space-customized", "true");
        }}
        onPreview={() => goTo("device")}
      />
    );
  }
  if (step === "event-dashboard") {
    return (
      <EventDashboardStep
        draft={eventDraft}
        customized={customized}
        onEdit={() => goTo("event-details")}
        onCustomize={() => goTo("space-editor")}
        onEnterHost={() => goTo("room", { role: "host" })}
        onJoinGuest={() => goTo("device")}
        onExit={() => window.location.assign("/")}
      />
    );
  }
  if (step === "event-details") {
    return (
      <SetupShell
        step="event-details"
        flow="organizer"
        title="Schedule your event"
        description="Add the essentials first. You can refine the space and permissions next."
      >
        <EventDetailsStep draft={eventDraft} updateDraft={updateDraft} onNext={() => goTo("event-space")} />
      </SetupShell>
    );
  }
  if (step === "event-space") {
    return (
      <SetupShell
        step="event-space"
        flow="organizer"
        title="Choose the experience"
        description="Start with an i3 template or prepare a custom GLB scene from Blender."
        onBack={() => goTo("event-details")}
      >
        <EventSpaceStep
          draft={eventDraft}
          updateDraft={updateDraft}
          onBack={() => goTo("event-details")}
          onNext={() => goTo("event-access")}
        />
      </SetupShell>
    );
  }
  if (step === "event-access") {
    return (
      <SetupShell
        step="event-access"
        flow="organizer"
        title="Control who can join"
        description="Set event access and the actions available to attendees."
        onBack={() => goTo("event-space")}
      >
        <EventAccessStep
          draft={eventDraft}
          updateDraft={updateDraft}
          onBack={() => goTo("event-space")}
          onNext={() => goTo("event-review")}
        />
      </SetupShell>
    );
  }
  if (step === "event-review") {
    return (
      <SetupShell
        step="event-review"
        flow="organizer"
        title="Review and publish"
        description="Confirm the schedule, space and access before sharing the event."
        onBack={() => goTo("event-access")}
      >
        <EventReviewStep
          draft={eventDraft}
          onBack={() => goTo("event-access")}
          onPublish={() => goTo("event-dashboard")}
        />
      </SetupShell>
    );
  }
  if (step === "audio") {
    return (
      <SetupShell
        step="audio"
        title="Check your audio"
        description="Choose your microphone and speakers before entering the event."
        onBack={() => goTo("device")}
      >
        <AudioStep onBack={() => goTo("device")} onNext={() => goTo("avatar")} />
      </SetupShell>
    );
  }
  if (step === "avatar") {
    return (
      <SetupShell
        step="avatar"
        title="Set up your avatar"
        description="Choose how you will appear to everyone in the shared space."
        onBack={() => goTo("audio")}
      >
        <AvatarStep onBack={() => goTo("audio")} onNext={() => goTo("room")} />
      </SetupShell>
    );
  }
  return (
    <SetupShell
      step="device"
      title="How would you like to join?"
      description="Start with your device. We will help you check audio and your avatar next."
    >
      <DeviceStep onNext={() => goTo("audio")} />
    </SetupShell>
  );
}
